// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Decides what part each selected element plays on a rack, from its properties alone.
 *
 * The eVolve properties come first because Revit's `Category` is not a discriminator in an eVolve model: a
 * strut channel, its threaded rods, and a conduit bend are all `Revit Conduit Fittings`.
 */

import { findMatchingProperty, type TypeSafeProperty } from 'components/Autodesk/extensions/propertyHelpers';
import { getBulkPropertiesBatched } from 'components/Autodesk/extensions/viewerHelpers';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { isNil } from 'helpers/isNotNil';

/**
 * What an element contributes to a rack measurement.
 *
 * - `conduit` — part of the conduit run: a straight length, a bend, a coupling, or a valve.
 * - `support` — a strut, rod, bracket, or end box, whose outer faces the dimension chain closes on.
 *
 * There is deliberately no `fitting` role — naming cannot say which run pieces can be measured, so
 * `findElementCenterline` makes that split from geometry instead.
 */
type RackElementRole = 'conduit' | 'support';

/**
 * eVolve category terms that mark a rack support. Checked before {@link EVOLVE_CONDUIT_TERMS}, since support
 * hardware is routinely named after what it carries — a `Conduit Hanger` is a support.
 */
const EVOLVE_SUPPORT_TERMS = [
  'strut',
  'channel',
  'hardware',
  'rod',
  'hanger',
  'bracket',
  'clamp',
  'trapeze',
  'anchor',
  'frame',
  'support',
];
/** eVolve category terms that mark part of a conduit run. */
const EVOLVE_CONDUIT_TERMS = ['conduit', 'pipe'];

/** Revit categories whose elements are part of a conduit run. Fittings and accessories hold bends and couplings. */
const REVIT_CONDUIT_RUN_TERMS = ['conduit', 'pipe', 'fitting', 'accessor'];

export const DIAMETER_PROPERTY_NAMES = ['Outside Diameter', 'Diameter(Trade Size)'] as const;
export type DiameterPropertyName = (typeof DIAMETER_PROPERTY_NAMES)[number];
/** The properties every classified element is read from. */
export const RACK_ROLE_PROPERTY_NAMES = ['Category', 'eVolve_CategoryId', 'eVolve_SubCategoryId'] as const;
export type RackRolePropertyName = (typeof RACK_ROLE_PROPERTY_NAMES)[number];

export type RackPropertyName = DiameterPropertyName | RackRolePropertyName;

/** The role-relevant properties of one element, lower-cased. Empty strings where the element has none. */
export type RackElementProperties = {
  category: string;
  evolveCategoryId: string;
  evolveSubCategoryId: string;
};

/** Everything read off one element in the single bulk-properties lookup. */
export type RackElementLookup = {
  /** Read in preference to `Size`, which every element carries but in formats like `4"ø-4"ø`. */
  diameters: {
    outsideDiameterFt: number | null;
    diameterFt: number | null;
  };
  /** The role of this element derived by its properties, or null if undecidable. */
  role: RackElementRole | null;
};

/** The role implied by the eVolve category properties, or `null` when the element carries none. */
const roleFromEvolveProperties = ({ evolveCategoryId, evolveSubCategoryId }: RackElementProperties) => {
  const evolveCategories = `${evolveCategoryId} ${evolveSubCategoryId}`;
  if (evolveCategories.trim() === '') return null;
  if (EVOLVE_SUPPORT_TERMS.some((supportTerm) => evolveCategories.includes(supportTerm))) return 'support';
  if (EVOLVE_CONDUIT_TERMS.some((conduitTerm) => evolveCategories.includes(conduitTerm))) return 'conduit';
  return null;
};

/** The role implied by the Revit category, or `null` when the element carries none. */
const roleFromRevitCategory = ({ category }: RackElementProperties) => {
  if (category === '') return null;
  return REVIT_CONDUIT_RUN_TERMS.some((conduitRunTerm) => category.includes(conduitRunTerm)) ? 'conduit' : 'support';
};

/**
 * The role an element's properties imply, or `null` when only its geometry can decide. Resolving what a
 * role means for measurement is the caller's job, since that means reading geometry.
 */
const rackRoleFromProperties = (properties: RackElementProperties): RackElementRole | null =>
  roleFromEvolveProperties(properties) ?? roleFromRevitCategory(properties);

/** Reads the role properties of one element out of a bulk-properties result. */
const toRackElementProperties = (properties: TypeSafeProperty<RackPropertyName>[]): RackElementProperties => {
  const valueOf = (name: RackRolePropertyName) =>
    String(findMatchingProperty(properties, name)?.displayValue ?? '').toLowerCase();

  return {
    category: valueOf('Category'),
    evolveCategoryId: valueOf('eVolve_CategoryId'),
    evolveSubCategoryId: valueOf('eVolve_SubCategoryId'),
  };
};

/** One diameter property of an element in feet, or `null` when it does not carry it. */
const readDiameterFt = (
  properties: TypeSafeProperty<RackPropertyName>[],
  name: DiameterPropertyName,
): number | null => {
  const property = findMatchingProperty(properties, name);
  if (isNil(property) || isNil(property.displayValue)) return null;

  const displayValue =
    typeof property.displayValue === 'number' ? property.displayValue : Number.parseFloat(property.displayValue);
  if (!Number.isFinite(displayValue)) return null;

  return property.units?.startsWith('autodesk.unit.unit:feet') ? displayValue : displayValue / 12;
};

/** Read the role and diameter properties of every selected element in one batched lookup. */
export const getRackElementLookup = async (
  model: Autodesk.Viewing.Model,
  dbIds: AutodeskDbId[],
): Promise<Map<AutodeskDbId, RackElementLookup>> => {
  const results = await getBulkPropertiesBatched<RackPropertyName>(model, dbIds, {
    propFilter: [...DIAMETER_PROPERTY_NAMES, ...RACK_ROLE_PROPERTY_NAMES],
  });

  return new Map(
    results.map(({ dbId, properties }) => [
      dbId,
      {
        diameters: {
          outsideDiameterFt: readDiameterFt(properties, 'Outside Diameter'),
          diameterFt: readDiameterFt(properties, 'Diameter(Trade Size)'),
        },
        role: rackRoleFromProperties(toRackElementProperties(properties)),
      },
    ]),
  );
};
